// Thin wrapper over @mediapipe/tasks-vision FaceLandmarker.
// Uses VIDEO running mode + detectForVideo so the engine drives a deterministic
// per-frame loop (simpler than LIVE_STREAM's async callback for our renderer).
// Lazy-imported so the ~6-8 MB wasm/model only downloads on button click.

import type { EngineConfig } from './types';
import type { LM } from './landmarks';

export interface TrackResult {
  /** 478 landmarks of the largest detected face, or null if none. */
  landmarks: LM[] | null;
  /** Number of faces MediaPipe returned this frame (for multi-face hint). */
  faceCount: number;
}

export interface FaceTracker {
  detect(video: HTMLVideoElement, timestampMs: number): TrackResult;
  close(): void;
}

function boundingArea(landmarks: LM[]): number {
  let minX = 1,
    minY = 1,
    maxX = 0,
    maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return (maxX - minX) * (maxY - minY);
}

export async function createFaceTracker(config: EngineConfig): Promise<FaceTracker> {
  // Dynamic import keeps MediaPipe out of the initial PDP bundle.
  const { FaceLandmarker, FilesetResolver } = await import('@mediapipe/tasks-vision');

  const resolver = await FilesetResolver.forVisionTasks(config.wasmBasePath);
  const delegate: 'GPU' | 'CPU' = config.delegate === 'CPU' ? 'CPU' : 'GPU';

  let landmarker;
  try {
    landmarker = await FaceLandmarker.createFromOptions(resolver, {
      baseOptions: { modelAssetPath: config.modelAssetPath, delegate },
      runningMode: 'VIDEO',
      numFaces: 2, // detect a second face only to surface the multi-face hint
      outputFacialTransformationMatrixes: false, // we anchor via landmark basis
      outputFaceBlendshapes: false,
    });
  } catch {
    // GPU delegate can fail on some drivers; fall back to CPU once.
    landmarker = await FaceLandmarker.createFromOptions(resolver, {
      baseOptions: { modelAssetPath: config.modelAssetPath, delegate: 'CPU' },
      runningMode: 'VIDEO',
      numFaces: 2,
    });
  }

  return {
    detect(video, timestampMs) {
      const res = landmarker.detectForVideo(video, timestampMs);
      const faces = res.faceLandmarks ?? [];
      if (faces.length === 0) return { landmarks: null, faceCount: 0 };

      // Track the largest (closest/most central) face.
      let best = faces[0];
      let bestArea = boundingArea(best);
      for (let i = 1; i < faces.length; i++) {
        const area = boundingArea(faces[i]);
        if (area > bestArea) {
          best = faces[i];
          bestArea = area;
        }
      }
      return { landmarks: best as LM[], faceCount: faces.length };
    },
    close() {
      landmarker.close();
    },
  };
}
